/**
 * COMPONENT_SPEC §6.8 `CutSheet`. OWNER: web-frontend-engineer.
 *
 * The document a person cuts a suit from. Rendered IN FULL, INLINE on
 * `SCR-ORDDET` — not behind a tab, not a download, not a modal — plus a print
 * view. Black on white, print-first: NO COLOUR CARRIES MEANING ANYWHERE ON THE
 * SHEET, because a workshop prints in mono.
 *
 * Never a partial sheet: a half-printed cut sheet is a cutting error waiting to
 * happen (`ST-CUTSHEET-FAILED`).
 */

import { t, tDynamic, type CopyKey } from '@pt/copy';
import type { CutSheetPayload } from '../lib/contracts.ts';
import { dateInWords, dateTimeInWords, metres, shortHash } from '@pt/view';
import { Button, FactLine, Statement } from './primitives.tsx';

export interface CutSheetProps {
  readonly sheet: CutSheetPayload;
  readonly onPrint?: () => void;
  readonly amended?: boolean;
}

export function CutSheet({ sheet, onPrint, amended = false }: CutSheetProps) {
  return (
    <section className="pt-cutsheet" aria-label={t('cutsheet.heading')}>
      <p className="pt-cutsheet__running">
        {t('cutsheet.runningHeader', {
          orderRef: sheet.orderRef,
          clientNameStaff: sheet.clientName,
        })}
      </p>

      {amended ? <FactLine>{t('orddet.amended.marker')}</FactLine> : null}

      {/* 5 September 2026, finding C2: a heading with no rows under it is not
          printed. The adapter drops such a block; the guard here makes the
          rule hold for any payload, not only for the adapter's. */}
      {sheet.blocks.filter((block) => block.rows.length > 0).map((block) => (
        <section className="pt-cutsheet__block" key={block.id}>
          <h2 className="pt-cutsheet__blockTitle">{tDynamic(block.titleKey)}</h2>
          <dl>
            {block.rows.map((row) => (
              <div className="pt-fact pt-fact--pair" key={`${block.id}-${row.label}`}>
                <dt className="pt-fact__label">{row.label}</dt>
                <dd className="pt-fact__value">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}

      {/* Derived finished measures, from the record and the ease set. */}
      {sheet.derivedMeasures.length === 0 ? null : (
        <section className="pt-cutsheet__block">
          <h2 className="pt-cutsheet__blockTitle">{t('cutsheet.derived.heading')}</h2>
          <dl>
            {sheet.derivedMeasures.map((row) => (
              <div className="pt-fact pt-fact--pair" key={row.label}>
                <dt className="pt-fact__label">{row.label}</dt>
                <dd className="pt-fact__value">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* ST-CUTSHEET-ASYMMETRY — PRINTS EVEN WHEN LEFT AND RIGHT ARE EQUAL, so
          its absence can never be mistaken for symmetry. Never averaged.
          Equal is not absent (finding C2, 5 Sept 2026): when NO pair was taken
          there is no measure to call equal, and a heading over an empty table
          saying "equal on every measure below" would be a false statement. The
          catalogue carries no sentence for "no pair was taken", so the block is
          not printed rather than authored here. */}
      {sheet.asymmetry.length === 0 ? null : (
      <section className="pt-cutsheet__block">
        <h2 className="pt-cutsheet__blockTitle">{t('cutsheet.asymmetry.heading')}</h2>
        {sheet.asymmetry.every((row) => row.difference == null) ? (
          <Statement>{t('cutsheet.asymmetry.equal')}</Statement>
        ) : null}
        <table className="pt-table">
          <thead>
            <tr>
              <th scope="col">{t('cutsheet.asymmetry.heading')}</th>
              <th scope="col" className="pt-table__num">
                {t('meas.field.half_back_left.label')}
              </th>
              <th scope="col" className="pt-table__num">
                {t('meas.field.half_back_right.label')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sheet.asymmetry.map((row) => (
              <tr key={row.fieldLabel}>
                <td>{row.fieldLabel}</td>
                <td className="pt-table__num">{row.left}</td>
                <td className="pt-table__num">{row.right}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      )}

      {/* ST-CUTSHEET-OVERRIDES — prints EVEN WHEN EMPTY. Absence must be
          affirmative, never inferred from a missing heading. */}
      <section className="pt-cutsheet__block">
        <h2 className="pt-cutsheet__blockTitle">{t('confirm.advisories.heading')}</h2>
        {sheet.overrides.length === 0 ? (
          <FactLine>{t('cutsheet.overrides.empty')}</FactLine>
        ) : (
          sheet.overrides.map((override) => (
            <FactLine key={override.constraintId}>
              {`${override.constraintId} · ${override.tailorName} · ${override.reason}`}
            </FactLine>
          ))
        )}
      </section>

      {/* ST-CUTSHEET-NOTE — verbatim, quoted, attributed, never edited. */}
      {sheet.clientNote == null ? null : (
        <section className="pt-cutsheet__block">
          <h2 className="pt-cutsheet__blockTitle">{t('cutsheet.block6')}</h2>
          <p className="pt-record__notes">{sheet.clientNote}</p>
          <Statement>{t('cutsheet.note.instruction')}</Statement>
        </section>
      )}

      {/* One figure, with its unit and its basis — or nothing. The old line
          printed the basis sentence and then "0.0" after it (finding C2,
          5 Sept 2026): a figure with no unit, from a key the server never
          wrote. `config.spec.metreage` is the catalogue's own "{metreage} m". */}
      {sheet.estimatedMetreage === null ? null : (
        <FactLine>
          {t('config.spec.metreage', { metreage: metres(sheet.estimatedMetreage) })}{' '}
          {t('cutsheet.estimate.statement')}
        </FactLine>
      )}
      <FactLine>
        {t('cutsheet.colour.statement')}{' '}
        {sheet.colourPlaceholder ? t('cutsheet.colour.placeholder') : ''}
      </FactLine>
      {sheet.napDirectional ? <FactLine>{t('cutsheet.nap')}</FactLine> : null}

      {/* Block 7 — the appendix, newest first. Not printed while empty. */}
      {sheet.appendix.length === 0 ? null : (
      <section className="pt-cutsheet__block">
        <h2 className="pt-cutsheet__blockTitle">{t('cutsheet.block7')}</h2>
        <ol>
          {sheet.appendix.map((entry) => (
            <li key={`${entry.event}-${entry.at}`} className="pt-timeline__entry">
              <span className="pt-timeline__date">{dateInWords(entry.at)}</span>
              <span className="pt-timeline__stage">{tDynamic(`event.${entry.event}.label`)}</span>
              <span>{entry.actorName}</span>
            </li>
          ))}
        </ol>
      </section>
      )}

      {/* The running footer names the record by its id and hash — a running
          footer on PAPER, where the cutter must be able to check the sheet in
          his hand against the record it was cut from. On a screen it is a
          UUID under a tailor's order (finding C2, 5 Sept 2026), so it prints
          and does not display. */}
      <p className="pt-cutsheet__running pt-printonly">
        {t('cutsheet.runningFooter', {
          recordId: sheet.recordId,
          contentHashShort: shortHash(sheet.contentHash),
          schemaVersion: sheet.schemaVersion,
        })}
      </p>
      {sheet.printedBy == null || sheet.printedAt == null ? null : (
        <FactLine>
          {t('cutsheet.printed', {
            printerName: sheet.printedBy,
            printedAt: dateTimeInWords(sheet.printedAt),
          })}
        </FactLine>
      )}

      {onPrint === undefined ? null : (
        <div className="pt-actions pt-noprint">
          <Button onClick={onPrint}>{t('global.action.print')}</Button>
        </div>
      )}
      <Statement>{t('cutsheet.access.simplification')}</Statement>
    </section>
  );
}

/** ST-CUTSHEET-FAILED / ST-ORDDET-CUTSHEET-FAILED — never a partial sheet. */
export function CutSheetFailed({ onRetry, messageKey }: { onRetry: () => void; messageKey: CopyKey }) {
  return (
    <section className="pt-cutsheet">
      <p className="pt-error__line">{t(messageKey)}</p>
      <div className="pt-actions">
        <Button onClick={onRetry}>{t('global.action.retry')}</Button>
      </div>
    </section>
  );
}
